import { numberToByteSlice } from '#cmp/specific/project/memory/memoryTabUtils.ts'
import type { ProgramDisplay, ProgramDisplayConfiguration } from '#lib/languages/programDisplay.ts'
import type { InjectedPeripheralOptions } from '#lib/languages/peripherals/peripheralSet.ts'
import type { Termination } from '#lib/languages/termination.ts'
import type { AvailableLanguages, Testcase, TestcaseResult } from '#lib/Project.svelte.ts'
import type { Annotations, BuildInput, BuildSources } from '#lib/projectFiles.ts'

export type StatusRegister = {
    name: string
    value: number
    prev: number
}
export type DiagnosticSeverity = 'error' | 'warning' | 'suggestion'
/** A Breakpoint on a line of a File: an assembly File's, or a C or C++ File's through its Source map. */
export type LineBreakpoint = { file: string; line: number }
/**
 * A Breakpoint on a **Listing**, held as the link-time address of the instruction in its Executable
 * rather than as a line, so it survives a Listing whose lines move
 * ([binary-import.md](../../../../../docs/design/binary-import.md), decision 5).
 */
export type AddressBreakpoint = { file: string; address: number }
export type SourceBreakpoint = LineBreakpoint | AddressBreakpoint

export function isLineBreakpoint(breakpoint: SourceBreakpoint): breakpoint is LineBreakpoint {
    return 'line' in breakpoint
}

/** The same Breakpoint: on the same File, at the same line or the same address. */
export function sameBreakpoint(a: SourceBreakpoint, b: SourceBreakpoint): boolean {
    if (a.file !== b.file) return false
    return isLineBreakpoint(a)
        ? isLineBreakpoint(b) && a.line === b.line
        : !isLineBreakpoint(b) && a.address === b.address
}

type DiagnosticBase = {
    file?: string
    lineIndex: number
    /** One-based UTF-16 column, matching Monaco. */
    column: number
    /** One-based, exclusive UTF-16 column. */
    endColumn?: number
    source?: string
    code?: string
    related?: {
        file: string
        lineIndex: number
        column: number
        endColumn: number
        message: string
    }[]
    message: string
    /** Actionable help supplied by the Core, shown directly after the message when present. */
    hint?: string
    formatted: string
    /** The changes the Core suggests, offered as quick fixes on it, the one to prefer first. */
    fixes?: DiagnosticFix[]
}

/**
 * A change a Core suggests for a diagnostic: `text` replaces the range, and an empty range inserts
 * it. Lines are zero-based and columns one-based UTF-16, as a diagnostic's are; the range may be in
 * another File than the diagnostic's.
 */
export type DiagnosticFix = {
    /** What applying it does: "Add `opt p=68020` at the top of the program". */
    title: string
    file: string
    lineIndex: number
    column: number
    endLineIndex: number
    /** One-based, exclusive. */
    endColumn: number
    /** What replaces the range, with its own line breaks. */
    text: string
}

/**
 * A compile/check-time finding. Only `error` severity blocks compilation, the other severities are
 * reported but let the program build and run.
 */
export type Diagnostic =
    | (DiagnosticBase & { severity: 'error' })
    | (DiagnosticBase & { severity: 'warning' })
    | (DiagnosticBase & { severity: 'suggestion' })

export type StackFrame = {
    name: string
    /**
     * What the frame's tooltip adds where `name` shortens it: a C++ function's signature and the
     * raw name it stands for ([cpp-demangling.md](../../../../../docs/design/cpp-demangling.md),
     * decision 11).
     */
    detail?: string
    address: bigint
    destination: bigint
    sp: bigint
    line: number
    file?: string
    color: string
}

export type MemoryTab = {
    id: number
    name: string
    address: bigint
    rowSize: number
    pageSize: number
    endianess: 'big' | 'little'
    data: DiffedMemory
    /**
     * Whether the user moved this view to an address of their own. Such a view keeps its address
     * across Stop and Build, where an untouched one is placed again by the language (the Stack
     * tab onto SP, for example).
     */
    userPlaced: boolean
}

export type DiffedMemory = {
    current: Uint8Array
    prevState: Uint8Array
    /**
     * The bytes of the page the Core could not read, which the view marks instead of showing a
     * value. Null, or absent, when the whole page was read.
     */
    unreadable?: UnreadableBytes | null
}

/** What a memory view found it could not read at `address`, one mask entry per byte of the page. */
export type UnreadableBytes = {
    address: bigint
    /** 1 for each byte that could not be read. */
    mask: Uint8Array
    /** Why the lowest of those bytes could not be read, as the Core put it. */
    reason: string
}

export enum RegisterSize {
    Byte = 1,
    Word = 2,
    Long = 4,
    Double = 8,
    /** 128 bits, the width of an x86 SSE register. */
    Quad = 16,
    /** 256 bits, the width of an x86 AVX register. */
    Oct = 32
}

export function numbersOfSizeToSlice(
    numbers: bigint[],
    bytes: number,
    endianess: 'big' | 'little' = 'big'
) {
    return numbers.flatMap((v) => numberToByteSlice(v, bytes, endianess))
}

export function makeGenericDiagnostic(error: string): Diagnostic {
    return {
        severity: 'error',
        lineIndex: 0,
        column: 0,
        message: error,
        formatted: error
    }
}
const DIAGNOSTIC_PREFIXES = {
    error: '',
    warning: 'Warning: ',
    suggestion: 'Suggestion: '
} satisfies Record<DiagnosticSeverity, string>

/**
 * Renders a diagnostic as a line of text. Errors stay bare, which is what the panels have always
 * shown, the other severities are announced unless the core already spelled the severity out.
 */
export function formatDiagnostic(diagnostic: Diagnostic): string {
    const prefix = DIAGNOSTIC_PREFIXES[diagnostic.severity]
    if (!prefix) return diagnostic.formatted
    if (diagnostic.formatted.toLowerCase().startsWith(diagnostic.severity)) {
        return diagnostic.formatted
    }
    return `${prefix}${diagnostic.formatted}`
}

export function toHexString(_value: bigint | number, _size: bigint | number): string {
    const value = BigInt(_value)
    const size = BigInt(_size)
    const bits = size * 8n
    const mask = (1n << bits) - 1n
    const hexDigits = Number(size * 2n)
    return (value & mask).toString(16).padStart(hexDigits, '0')
}

export function makeRegister(name: string, v: bigint | number, _size: RegisterSize) {
    let value = $state(BigInt(v))
    let prev = $state(BigInt(v))
    let size = $state(BigInt(_size))

    function setValue(v: number | bigint) {
        prev = value
        value = BigInt(v)
    }

    function toHex() {
        return toHexString(value, size)
    }

    function setSize(newSize: RegisterSize) {
        size = BigInt(newSize)
    }

    return {
        name,
        get value() {
            return value
        },
        get prev() {
            return prev
        },
        //the renderers need the width a register ended up with, which is not always the one its
        //Register file declares: the Z80 adapter narrows `a` to a byte after the file is built
        get size() {
            return size
        },
        setSize,
        setValue,
        toHex
    }
}

/**
 * One entry of a Core's Undo history: either an instruction the program ran, or a Poke a person or
 * the coding agent made between two instructions
 * ([the design record](../../../../../docs/design/pokes.md)). A poke step carries no source location,
 * since no instruction ran, and the panels read `kind` to tell the two apart.
 */
export type ExecutionStep = {
    /**
     * An instruction the program ran, a Poke the person made between two of them, or an IRQ the
     * processor took between two of them, which ran no instruction of its own (M68K,
     * [m68k-machine.md](../../../../../docs/design/m68k-machine.md), decision 16).
     */
    kind: 'instruction' | 'poke' | 'irq'
    /** False for a visible native History barrier whose effects are not journaled. */
    undoable?: boolean
    mutations: MutationOperation[]
    pc: number
    old_ccr: {
        bits: number
    }
    new_ccr: {
        bits: number
    }
    line: number
    file?: string
    /** What a Poke wrote, one entry per register and per run of consecutive memory bytes. */
    writes?: PokeWrite[]
    /**
     * What ran, decoded from the words the step ran, which a later write into code leaves alone,
     * where the source does not say: an instruction on no line, as in data the program ran into, or
     * on a line whose bytes the program or a Poke changed since the Build (M68K,
     * [m68k-machine.md](../../../../../docs/design/m68k-machine.md), decision 28). Absent on a step
     * that ran nothing, an instruction fetched from an odd address.
     */
    disassembly?: string
    /**
     * What a Poke did, in words, when it is more than the values it wrote: a patch of an
     * Executable's instruction from its Listing reads `Patched jne → je at 0x5555555551f8`.
     */
    title?: string
    /**
     * The clock cycles the step took, for a Target that counts them (M68K, with the 68000's
     * timing: [m68k-machine.md](../../../../../docs/design/m68k-machine.md), decision 30). Absent
     * on a Poke, which ran nothing, and on every step of a Target that does not count.
     */
    cycles?: number
    /**
     * Whether `cycles` is an estimate rather than the processor's own time: an M68K program for the
     * 68020, whose manual gives each instruction's time for its cache case, as the row marks it.
     */
    cyclesEstimate?: boolean
    /**
     * Set on the instruction that started a Step through library code: the library function it
     * entered (`printf`, `puts in libc.so.6`) and every instruction the Step ran, which one Undo
     * takes back together.
     */
    stretch?: LibraryStretch
}

/** A Step's stretch of library code, as the History names it. */
export type LibraryStretch = {
    /**
     * What the Step called: `printf`, `puts in libc.so.6`,
     * `std::ostream::operator<< in libstdc++.so.6`.
     */
    library: string
    /**
     * Where `library` shortens a C++ name, what its tooltip adds: the signature and the raw name
     * ([cpp-demangling.md](../../../../../docs/design/cpp-demangling.md), decision 11).
     */
    detail?: string
    instructions: number
}

/**
 * One value a Poke changed, as the panels show it: unsigned bit patterns, and register names in the
 * editor's own spelling for that language, which is what the Register files list.
 */
export type PokeWrite =
    | { type: 'register'; name: string; old: bigint; new: bigint }
    | { type: 'memory'; address: bigint; old: number[]; new: number[] }

/** One register write of a Poke; several of them in one call are one step of the Undo history. */
export type RegisterPoke = { register: string; value: bigint }

/**
 * One thing an instruction did, as its Core's history reports it. A write carries what it replaced
 * when the Core keeps it, which is what undo puts back, and what it wrote when the Core reports
 * that too; neither is reconstructed here, so a value a Core cannot hand over is simply absent
 * (RARS hands a 64 bit register's old value over as a 32 bit int, so RV64 has none).
 */
export type MutationOperation =
    | {
          type: 'WriteRegister'
          value: {
              register: string
              old?: bigint
              new?: bigint
              size: RegisterSize
          }
      }
    | {
          type: 'WriteMemory'
          value: {
              address: bigint
              old?: bigint
              new?: bigint
              size: RegisterSize
          }
      }
    | {
          type: 'WriteMemoryBytes'
          value: {
              address: bigint
              old: number[]
              new?: number[]
          }
      }
    | {
          type: 'PopCallStack'
          value: {
              to: bigint
              from: bigint
          }
      }
    | {
          type: 'PushCallStack'
          value: {
              to: bigint
              from: bigint
          }
      }
    /**
     * The processor took an exception: it pushed a frame and jumped through `vector` to the
     * handler at `handler`, named `label` when a label is there (M68K,
     * [m68k-machine.md](../../../../../docs/design/m68k-machine.md), decision 28).
     */
    | {
          type: 'TakeException'
          value: {
              vector: number
              /** What the vector is for, as a learner reads it: `Divide by zero`, `TRAP #3`. */
              name: string
              from: bigint
              handler: bigint
              label?: string
          }
      }
    /** A return from an exception, `rte` on M68K, from the instruction at `from` to `to`. */
    | {
          type: 'ReturnFromException'
          value: {
              to: bigint
              from: bigint
          }
      }
    | {
          type: 'Other'
          value: string
      }

export type Register = ReturnType<typeof makeRegister>

/**
 * How a Register file's values are read for display. `hex` shows the bit pattern grouped by size,
 * the two float Formats decode the bits as IEEE 754. A file offers the Formats that make sense for
 * its registers and the first one it offers is its default.
 */
export type RegisterFormat = 'hex' | 'single' | 'double'

/**
 * One register of a Register file. Both fields are overrides for files that are not uniform:
 * `size` defaults to the file's size (x86's SSE file is 128 bits wide but `mxcsr` is 32), and
 * `kind` defaults to `float` when the file offers a float Format and to `integer` otherwise, so an
 * integer control register keeps its hexadecimal rendering inside a floating-point file.
 */
export type RegisterFileRegister = {
    name: string
    size?: RegisterSize
    kind?: 'integer' | 'float'
    /**
     * What the register is, which its name shows on hover: the M68K 68020's ISP says it is the
     * 68000's SSP. A register with none has no hover.
     */
    description?: string
}

/** A `RegisterFileRegister` with the file's defaults filled in: what the renderers read. */
export type ResolvedRegisterFileRegister = Required<Omit<RegisterFileRegister, 'description'>> &
    Pick<RegisterFileRegister, 'description'>

/**
 * What an adapter declares about a Register file it can read out of its Core
 * ([the design record](../../../../../docs/design/register-files.md)): a named, ordered set of registers
 * that share a width and a way of being read, optionally with a row of Status flags of its own.
 */
export type RegisterFileDescriptor = {
    id: string
    label: string
    size: RegisterSize
    /** The Formats this file offers, the first of which is its default. */
    formats: readonly RegisterFormat[]
    registers: readonly RegisterFileRegister[]
    /**
     * MIPS: a double is read from the even/odd register pair, low word in the even register, so
     * only the even rows show a value and the odd ones show nothing, as MARS's Double column does.
     */
    pairedDoubles?: boolean
    /**
     * RISC-V: a 64 bit register holds a single only when its high word is all ones, and anything
     * else reads as NaN in the single Format, as RARS shows it.
     */
    nanBoxedSingles?: boolean
    /** The file's own Status flags, shown above its registers. MIPS's FPU has eight, named 0..7. */
    flagNames?: readonly string[]
    hiddenRegisters?: readonly string[]
}

/**
 * A Register file as the panels and the coding agent see it: the descriptor, its registers read
 * back out of the Core with the previous value each keeps for change highlighting, and its own
 * Status flags. `layout` is the descriptor's register list with every default filled in, kept
 * beside the values because a `Register` carries no kind.
 */
export type RegisterFile = Omit<RegisterFileDescriptor, 'registers'> & {
    layout: readonly ResolvedRegisterFileRegister[]
    registers: Register[]
    flags: StatusRegister[]
    /**
     * One entry per register, true for a row that holds no value at this refresh and is shown
     * blank, the way gdb prints Empty for an x87 stack slot the tag word marks empty. The
     * register keeps the stale bits underneath, a hover away. Empty when a file never blanks.
     */
    blanks: boolean[]
}

/**
 * A file that offers any float Format holds floating-point registers, unless one of them says
 * otherwise.
 */
export function defaultRegisterKind(formats: readonly RegisterFormat[]): 'integer' | 'float' {
    return formats.some((format) => format !== 'hex') ? 'float' : 'integer'
}

export function resolveRegisterFileLayout(
    file: Pick<RegisterFileDescriptor, 'size' | 'formats' | 'registers'>
): ResolvedRegisterFileRegister[] {
    const kind = defaultRegisterKind(file.formats)
    return file.registers.map((register) => ({
        name: register.name,
        size: register.size ?? file.size,
        kind: register.kind ?? kind,
        ...(register.description ? { description: register.description } : {})
    }))
}

/** Core layout facts; all ranges are half-open and addresses stay lossless. */
export type MemoryRunKind = 'code' | 'data' | 'reserved'
export type MemoryLayoutItem = {
    start: bigint
    length: bigint
    kind: MemoryRunKind
    section: string
    alignment?: bigint
}
export type DataLabel = {
    /** The assembly unit that defines this symbol, when the Core reports it. */
    file?: string
    name: string
    address: bigint
    section?: string
    fromLibrary: boolean
    displayName?: string
    preview?: string
}
export type MemoryLayout = {
    sections: { name: string; runs: Omit<MemoryLayoutItem, 'section' | 'alignment'>[] }[]
    dataLabels: DataLabel[]
    /** Memory the Core reads but takes no Pokes into, such as a text segment of statements. */
    readOnly?: ReadOnlyMemory[]
    /**
     * Memory the machine itself gives a meaning, whatever the program placed there: the 68000's
     * exception vectors, and the ranges of an M68K program's memory map. The vectors' names are
     * data labels of their region, in its section.
     */
    machineRegions?: MachineRegion[]
    /** Where the program's Arguments are, once the Core has stored them (`withArgumentsArea`). */
    arguments?: { name: string; start: bigint; end: bigint }
}
/**
 * What a program starts with above its stack, as the Core wrote it at the program's start: the
 * `argv` array and its strings, and on x86 the rest of the Linux process-start block. Its parts
 * become data labels of its region, so the memory view names them as it names variables.
 */
export type ArgumentsArea = {
    /** The region's name: "Arguments", or "Arguments and environment" on x86. */
    name: string
    start: bigint
    end: bigint
    /** Each part where it starts; `text` for a NUL-terminated string, which the picker previews. */
    parts: { name: string; address: bigint; text?: boolean }[]
}
/** A half-open range of memory that takes no Pokes, and why, which the memory view shows. */
export type ReadOnlyMemory = { start: bigint; end: bigint; reason: string }
export type HeapBounds = { start: bigint; end: bigint }
export type DeviceRegion = { name: string; start: bigint; end: bigint }
/**
 * The kinds of memory the machine defines: an exception vector table, and the four ranges of
 * EASy68K's memory map, ROM (the program's writes are ignored), read only (a write raises a bus
 * error), protected (any access from user mode does) and invalid (any access does).
 */
export type MachineRegionKind = 'vectors' | 'rom' | 'readOnly' | 'protected' | 'invalid'
/** A half-open range the machine defines, with what it means, which the memory view's hover says. */
export type MachineRegion = {
    name: string
    start: bigint
    end: bigint
    kind: MachineRegionKind
    reason: string
}
export type MemoryRegion = DeviceRegion & {
    id: string
    kind: MemoryRunKind | 'heap' | 'stack' | 'arguments' | 'device' | MachineRegionKind
    section?: string
    /** The live stack jumps to SP, including when its extent is empty. */
    destination?: bigint
    /** What the memory means to the program, for a region the machine defines. */
    reason?: string
}

export type EmulatorDecoration = {
    type: 'below-line'
    note?: string
    belowLine: number
    file?: string
    md: string
    /** Generated instructions shown below one original source line, in assembly-address order. */
    instructions?: { address: bigint; code: string }[]
}

/**
 * A line whose bytes in memory are no longer what its Build placed, because the program or a Poke
 * wrote over them: where it is, and what runs there now, as the Core disassembles memory. The line
 * keeps its place in the source; only what it runs has changed
 * ([m68k-machine.md](../../../../../docs/design/m68k-machine.md), decision 27).
 */
export type ChangedCode = {
    file: string
    /** Zero-based source line. */
    line: number
    address: bigint
    runsNow: string
}

export type BuildArtifact = {
    file: string
    /** Zero-based source line. */
    line: number
    address: bigint
    /** Emitted instruction bytes or machine word, written in hexadecimal, when the Core exposes it. */
    opcode?: string
    /**
     * What the line assembled as, in the Core's own syntax, where the assembler chose something
     * the line did not write: another mnemonic (`add.l #1,d0` is `ADDQ.L #1,D0`), a branch size
     * (`bra loop` is `BRA.S loop`) or an address in a word. The M68K Core says so; the others
     * show what they expand a line into as Assembled instructions below it instead.
     */
    assembledAs?: string
    /**
     * What runs at `address` now, as the Core disassembles memory, when the program or a Poke has
     * changed the bytes the Build placed there: the Workbench adds it from `changedCode` for the
     * hover, and a Build never sets it.
     */
    runsNow?: string
}

export type EmulatorInterrupt = {
    type: string
    message?: string
}

/**
 * A cycle counter as the panels show it: the clock cycles the program has taken since it started,
 * or since it last cleared the counter (M68K's `trap #15` task 30), and what the newest step took.
 */
export type CycleCount = {
    total: bigint
    /** The newest step's cycles; undefined without an Undo history, and after a Poke. */
    last?: number
    /**
     * Whether the counts are an estimate rather than the processor's own time: an M68K program for
     * the 68020, whose manual can give only each instruction's cache case, since on the chip the
     * time of one depends on its neighbours ([m68k-machine.md](../../../../../docs/design/m68k-machine.md),
     * decisions 3 and 30). The panels mark them "≈".
     */
    estimate?: boolean
}

export type BaseEmulatorState = {
    code: string
    systemSize: RegisterSize
    compiledCode?: string
    /**
     * The processor the Build's program is written for, while it is one of the Target's later
     * processors: an M68K program's `opt p=68020`; or the one a Target starts on with what an
     * option added to it, `68000 with bit fields` for `opt bit`. Undefined for the processor a
     * Target starts on as it is, and before a Build.
     */
    processor?: string
    registers: Register[]
    /**
     * Every Register file the Emulator exposes. The CPU file is always element 0 and its
     * `registers` is the very array `registers` above is, so a caller that only knows about the
     * general registers keeps reading them where it always did.
     */
    registerFiles: RegisterFile[]
    startingRegisterNames: string[]
    hiddenRegisters: string[]
    decorations: EmulatorDecoration[]
    buildArtifacts: BuildArtifact[]
    /** The lines whose code the program or a Poke changed since the Build, see `ChangedCode`. */
    changedCode: ChangedCode[]
    statusRegisters: StatusRegister[]
    errors: string[]
    compilerDiagnostics: Diagnostic[]
    terminated: boolean
    /**
     * How the program ended, while `terminated` is true: an exit with its status, an end past the
     * last instruction, a signal or a runtime error. Undefined while it runs, and after an Undo
     * that takes the end back.
     */
    termination?: Termination
    latestSteps: ExecutionStep[]
    callStack: StackFrame[]
    line: number
    currentFile: string
    executionTime: number
    sp: bigint
    pc: bigint
    stdOut: string
    canExecute: boolean
    canUndo: boolean
    /**
     * Whether the last Run returned because Pause was requested. The Core is idle and available
     * for Step, Undo or another Run, as after a breakpoint.
     */
    paused: boolean
    /**
     * What the program waits for while it waits on its own rather than on the person, as the
     * adapter says it (`_getRunStatus`): an M68K `stop`'s "Stopped: waiting for an interrupt above
     * level 3". Undefined the rest of the time.
     */
    runStatus?: string
    /**
     * The cycle counter, for a Target that counts the clock cycles its processor takes: the M68K's
     * shows above the PC as "Cycles 12,345 (+8)" and ends the Log's exit line
     * ([m68k-machine.md](../../../../../docs/design/m68k-machine.md), decision 30). Undefined for
     * every other Target, and before a Build.
     */
    cycles?: CycleCount
    breakpoints: SourceBreakpoint[]
    interrupt?: EmulatorInterrupt
    memory: {
        global: MemoryTab
        tabs: MemoryTab[]
    }
}

/**
 * Values `GenericEmulator` derives from `BaseEmulatorState` instead of storing: `compilerErrors` is
 * the error-severity subset of `compilerDiagnostics`, so anything gating on "the code does not
 * compile" stays correct without having to filter by severity itself.
 */
export type BaseEmulatorDerivedState = {
    resolveMemoryLabel(name: string): bigint | undefined
    readonly compilerErrors: Diagnostic[]
    readonly memoryRegions: readonly MemoryRegion[]
    readonly dataLabels: readonly DataLabel[]
    /**
     * The labels and strings of the Debug session's program, as the Build loaded it; undefined
     * outside a Debug session and for a loaded Executable, which the Symbols panel reads from its
     * Listing instead.
     */
    readonly programSymbols?: import('./programSymbols').ProgramSymbols
    readonly readOnlyMemory: readonly ReadOnlyMemory[]
    /** Immutable Files and Entry used by the current executable, retained until Stop. */
    readonly buildSources?: BuildSources
    /** The Runtime library members that executable linked against, read-only, under `@runtime/`. */
    readonly buildLibraryFiles?: import('#lib/projectFiles.ts').ProjectFiles
    /** What the Log should say about the current program's run, oldest first; see `EmulatorNote`. */
    readonly notes: readonly EmulatorNote[]
    /**
     * What the Audio Peripheral is doing, for the execution dock's speaker button: how many Notes
     * and Sounds have played since the Emulator was made, whether a program waits for the sound
     * bank, and how much longer something is heard.
     */
    readonly audioActivity: import('#lib/languages/peripherals/audio/Audio.ts').AudioActivity
    /**
     * What the Build in progress is waiting for, in words, such as "Downloading libc.so.6…";
     * undefined when there is nothing more to say than that it builds.
     */
    readonly buildPhase?: string
    /**
     * x86 only: the Listings of Executables this Emulator generates, and in `session` those of the
     * running program and the libraries it loaded, under `@lib/`.
     */
    readonly listings?: import('#lib/listing/listingDocuments.svelte.ts').ListingDocuments
    /**
     * x86 only: patches of the running Executable's instructions from its Listing, written as
     * Pokes, and the Listing lines they changed.
     */
    readonly patcher?: import('#lib/listing/listingPatcher.svelte.ts').ListingPatcher
}

export enum InterpreterStatus {
    Running = 0,
    Interrupt = 1,
    Terminated = 2,
    TerminatedWithException = 3
}

let currentTabId = 0

export function createMemoryTab(
    pageSize: number,
    name: string,
    address: bigint,
    rowSize: number,
    initialValue: number,
    endianess: 'big' | 'little'
): MemoryTab {
    return {
        name,
        address,
        id: currentTabId++,
        rowSize,
        pageSize,
        endianess,
        userPlaced: false,
        data: {
            current: new Uint8Array(pageSize).fill(initialValue),
            prevState: new Uint8Array(pageSize).fill(initialValue),
            unreadable: null
        }
    }
}

/**
 * The same view with its contents back to the initial value. It keeps its id, so the window
 * showing it is not remounted (and collapsed) by a Stop, and it keeps its address.
 */
export function resetMemoryTab(tab: MemoryTab, initialValue: number): MemoryTab {
    return {
        ...tab,
        data: {
            current: new Uint8Array(tab.pageSize).fill(initialValue),
            prevState: new Uint8Array(tab.pageSize).fill(initialValue),
            unreadable: null
        }
    }
}

export function makeLabelColor(index: number, _address: number) {
    return `hsl(${(index * 137) % 360}, 40%, 60%)`
}

export function makeColorizedLabels(labels: StackFrame[]): ColorizedLabel[] {
    //same address and index should always be the same color
    return labels.map((address) => ({
        address: address.address,
        sp: address.sp,
        color: address.color
    }))
}

export type ColorizedLabel = {
    address: bigint
    sp: bigint
    color: string
}

export type EmulatorSettings = {
    language?: AvailableLanguages
    /** False when a Project-scoped Worker owns live diagnostics for this Emulator. */
    automaticChecking?: boolean
    globalPageSize?: number
    globalPageElementsPerRow?: number
    baseAddress?: bigint
    stackAddress?: bigint
    initialMemoryValue?: number
    /**
     * The Screen, Keyboard, Mouse, clock and Random source the Emulator runs on
     * ([ADR 0004](../../../../../docs/adr/0004-inject-screens-at-emulator-boundary.md)). The GUI creates
     * them so it can bind its widgets to the very instances the Core uses; anything left out is
     * built from the language defaults, which is what every caller that does not care gets.
     */
    peripherals?: InjectedPeripheralOptions
    /**
     * MIPS, RISC-V and x86: the Display configuration of the Program the Emulator was opened for,
     * MARS's and RARS's five bitmap-display parameters or x86's framebuffer size. The M68K and the
     * Z80 configure their Screen from the program itself, and an Emulator that is given none starts
     * from its Target's defaults.
     */
    display?: ProgramDisplay
    /**
     * The Screen undo journal's budget in megabytes, a Setting of the Project the Emulator was
     * opened for ([ADR 0014](../../../../../docs/adr/0014-settings-split-by-effect.md)); the language's
     * default when left out. Changed later with `setScreenHistoryBudgetMb`.
     */
    screenHistoryBudgetMb?: number
    /** FileSystem inverse-history budget in megabytes, applied at Build. */
    fileSystemHistoryBudgetMb?: number
    /**
     * x86, MIPS and RISC-V: the Project's **Arguments** as typed on the Command line, passed at
     * every start; none when left out. Changed later with `setProgramArguments`.
     */
    programArguments?: string
}

/**
 * Something the **Log** should say about a program's run that neither its output nor its exit
 * says: a system call the Core does not implement, an instruction it cannot run, a library it
 * does not bundle ([binary-import.md](../../../../../docs/design/binary-import.md), decision 17).
 * `id` grows across the Emulator's life, so a reader that has seen up to one knows which are new.
 */
export type EmulatorNote = {
    id: number
    tone: 'info' | 'warning' | 'error'
    text: string
}

export type BaseEmulatorActions = {
    /** A Build whose `signal` is aborted before its turn at the Core comes does not start. */
    compile: (
        historySize: number,
        sourceOverride?: BuildInput,
        signal?: AbortSignal
    ) => Promise<void>
    step: () => Promise<boolean>
    run: (haltLimit: number) => Promise<InterpreterStatus>
    /**
     * A Run that also stops before any of `targets`, temporary Breakpoints kept only for it: Run
     * to Cursor.
     */
    runTo: (targets: readonly SourceBreakpoint[], haltLimit: number) => Promise<InterpreterStatus>
    setGlobalMemoryAddress: (address: bigint) => void
    setGlobalMemorySize: (pageSize: number, rowSize: number) => void
    setCode: (code: string) => void
    setSources: (sources: BuildInput) => void
    check: () => Promise<Diagnostic[]>
    clear: () => void
    setTabMemoryAddress: (address: bigint, tabId: number) => void
    toggleBreakpoint: (line: number, file?: string) => void
    /** A Breakpoint on a Listing: the link-time address of an instruction of that Executable. */
    toggleAddressBreakpoint: (address: number, file: string) => void
    /** Translates Breakpoints on Files the Core never sees into the ones it runs with. */
    setBreakpointResolver: (
        resolver: ((breakpoints: readonly SourceBreakpoint[]) => SourceBreakpoint[]) | undefined
    ) => void
    /**
     * Where the program a person's Build links goes: x86 only, saved as a File of the Project by
     * the Workbench that owns it, else only shown to the running program; see `BuildOutputHost`.
     */
    setBuildOutputHost: (host: import('#lib/buildOutput.ts').BuildOutputHost | undefined) => void
    /** Returns how many instructions were actually rolled back, which can be fewer than asked. */
    undo: (amount?: number) => number
    /**
     * The Core's own decoding of the instruction at an address, whatever line shows it: what runs
     * there when the program jumps into the middle of a Listing's instruction. Undefined when the
     * Core cannot read one there.
     */
    instructionCodeAt?: (address: bigint) => string | undefined
    /**
     * Whether a Poke is possible right now, which is exactly when Step is
     * ([the design record](../../../../../docs/design/pokes.md)): after a Build, with no Interrupt
     * pending, the program not terminated and no Run, Step or input handler owning the Core. The
     * panels bind to it, so it is reactive.
     */
    readonly canPoke: boolean
    /** Whether that one register of that Register file may be poked; the PC never may. */
    canPokeRegister: (fileId: string, register: string) => boolean
    /**
     * Pokes one or more registers of one Register file as a single step of the Undo history. False
     * when the Poke was refused or changed nothing; throws when a value does not fit its register.
     */
    pokeRegisters: (fileId: string, writes: RegisterPoke[]) => boolean
    /** The same for a run of memory bytes, which is one step however many bytes it holds. */
    pokeMemory: (address: bigint, bytes: Uint8Array) => boolean
    /**
     * Ends the current Run at its next slice boundary, preserving the program and undo history.
     * Does nothing when no run is in flight.
     */
    pause: () => void
    resetSelectedLine: () => void
    dispose: () => void
    test: (
        sources: BuildInput,
        testcases: Testcase[],
        haltLimit: number,
        historySize?: number
    ) => Promise<TestcaseResult[]>
    getLineFromAddress: (address: bigint) => number
    getSourceLocationFromAddress: (address: bigint) => { file: string; line: number } | null
    readMemoryBytes: (address: bigint, length: number) => Uint8Array
    /**
     * A new Screen undo budget, applied on the next clear, which is what a Build starts with: a
     * Setting takes effect at the next Build and never resizes anything under a running program.
     */
    /** The Entry path of the sources currently set. */
    entry: string
    /**
     * The Arguments every later start passes the program, as typed on the Command line; a Testcase
     * with Arguments of its own passes those instead. Only x86 programs take them.
     */
    setProgramArguments: (text: string) => void
    setScreenHistoryBudgetMb: (megabytes: number) => void
    /** The same for the FileSystem's Undo budget, likewise read by the next Build's session. */
    setFileSystemHistoryBudgetMb: (megabytes: number) => void
    /**
     * MIPS, RISC-V and x86: applies a Display configuration at once, MARS's five bitmap-display
     * parameters with a re-sync from memory as the tool does, or x86's framebuffer size for the next
     * program. Each Emulator reads the shape of its own Target. Absent on the M68K and the Z80, whose
     * Screen is the program's to configure.
     */
    setDisplay?: (display: ProgramDisplay) => void
    /**
     * x86 only: the Annotations the File of the Executable a Debug session runs has now, which
     * its running Listing is made again with ([annotations-and-search.md](../../../../../docs/design/annotations-and-search.md),
     * decision 22): a rename or a comment made during the session shows at once.
     */
    setExecutableAnnotations?: (path: string, annotations: Annotations | undefined) => void
    /**
     * MIPS, RISC-V and x86: the display the Screen is configured with right now, and whether the
     * last Build read it out of the program's own `@screen` comment directive rather than from the
     * user. The GUI pulls it after a Build so the popover shows what the source asked for.
     */
    getDisplay?: () => ProgramDisplayConfiguration
}
